import { Avatar, Badge, IconNames } from '@dixa/lib-shared-habla';
import * as Popover from '@radix-ui/react-popover';
import { useHasAccess } from 'lib-shared-access-control';
import type {
  AsyncStatus,
  LoadDescriptionT,
  Presence,
} from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import capitalize from 'lodash/capitalize';
import noop from 'lodash/noop';
import { ComponentProps } from 'react';

import {
  PresenceDropdownItem,
  PresenceDropdownSeparator,
  PresenceToggle,
} from '../../profile/components/PresenceDropdownItem';

import PresenceOverlayChannels from './PresenceOverlayChannels';
import styles from './presenceOverlayPopover.module.scss';

type PropsT = {
  user: User;
  presence: Presence;
  acwConversationNode: JSX.Element | null;
  hasChannelPhone: boolean;
  lastSeen: string | null;
  onInternalCallClickHandler: () => void;
  meta: {
    asyncStatus: AsyncStatus;
    toggleSimultaneousRing: AsyncStatus;
    errors: Array<string> | null;
    offline: boolean;
  };
  presenceSetOthersSimultaneousRinging: (args: any) => Promise<void>;
  isOwnAgent: boolean;
  internalBtnText: string;
  disabledCall: string;
  showUserModal: (payload: { user: User }) => void;
  conversationsByChannel: Partial<Record<IconNames, LoadDescriptionT[]>>;
  handlePresenceLinkClick: (csid: number) => void;
  children: JSX.Element;
};

export const PresenceOverlayPopover = ({
  user,
  presence,
  acwConversationNode,
  hasChannelPhone,
  lastSeen,
  onInternalCallClickHandler,
  meta,
  presenceSetOthersSimultaneousRinging,
  isOwnAgent,
  internalBtnText,
  disabledCall,
  showUserModal,
  conversationsByChannel,
  handlePresenceLinkClick,
  children,
}: PropsT): JSX.Element => {
  const hasConversationsAssigned = Object.keys(conversationsByChannel).some(
    (channel) => conversationsByChannel[channel].length > 0,
  );
  const hasOrganizationMemberAccess = useHasAccess('organization.member');
  const hasConversationReplyAccess = useHasAccess('conversation.reply');

  const presenceBadgeMap: Record<
    Presence['status'],
    ComponentProps<typeof Badge>['variant']
  > = {
    working: 'success',
    away: 'warning',
    offline: 'secondary',
  };

  const hasPhone = !!user.phone_number && user.phone_number.length > 0;
  const isSimultaneousRingLoading = meta.toggleSimultaneousRing === 'loading';

  return (
    <Popover.Root>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className={styles.dropdown} side="right">
          <div className={styles.nameHolder}>
            <div className={styles.avatarHolder}>
              <Avatar user={user} />
            </div>

            <h4>
              {user.name !== undefined && user.name !== null
                ? user.name.replace(/ .*/, '')
                : ''}
            </h4>

            {presence.status && (
              <Badge
                variant={presenceBadgeMap[presence.status] ?? 'secondary'}
                size="medium"
              >
                {capitalize(presence.status)}
              </Badge>
            )}
          </div>
          <PresenceOverlayChannels presence={presence} />
          <div>
            {acwConversationNode && (
              <div className={styles.smallHolder}>
                <p>{acwConversationNode}</p>
              </div>
            )}

            {lastSeen && (
              <div className={styles.smallHolder}>
                <p>{lastSeen}</p>
              </div>
            )}

            {hasChannelPhone && (
              <>
                {hasConversationReplyAccess && hasOrganizationMemberAccess && (
                  <PresenceDropdownItem
                    icon="simultaneous"
                    text="Simultaneous ring"
                    tooltipText={
                      hasPhone
                        ? `Toggle simultaneous ringing for ${
                            user && user.name ? user.name : 'agent'
                          }`
                        : 'Simultaneous ringing is turned off (phone number needed)'
                    }
                    disabled={!hasPhone}
                  >
                    <PresenceToggle
                      id="shouldOpenSimultaneous"
                      disabled={hasPhone && isSimultaneousRingLoading}
                      active={!!presence.presence_status.simultaneous_ringing}
                      onToggle={
                        hasPhone && !isSimultaneousRingLoading
                          ? () => {
                              presenceSetOthersSimultaneousRinging({
                                userId: user.id,
                                enabled:
                                  !presence.presence_status
                                    .simultaneous_ringing,
                              });
                            }
                          : noop
                      }
                    />
                  </PresenceDropdownItem>
                )}

                {hasConversationReplyAccess && !isOwnAgent && (
                  <PresenceDropdownItem
                    icon="phone"
                    text={internalBtnText}
                    tooltipText={`Make internal call to ${user.name}`}
                    disabled={disabledCall !== ''}
                    onClick={onInternalCallClickHandler}
                  />
                )}
              </>
            )}

            {(isOwnAgent || hasOrganizationMemberAccess) && (
              <PresenceDropdownItem
                icon="pen"
                text="Edit agent"
                onClick={() => {
                  showUserModal({ user });
                }}
              />
            )}

            <PresenceDropdownSeparator />

            {!hasConversationsAssigned ? (
              <PresenceDropdownItem
                icon="chat-slash"
                text="No conversations here"
                disabled={true}
              />
            ) : (
              (Object.keys(conversationsByChannel) as IconNames[]).map(
                (icon) => {
                  return conversationsByChannel[icon]?.map((item, index) => (
                    <PresenceDropdownItem
                      key={index}
                      icon={icon}
                      text={`Open #${item.csid}`}
                      onClick={() => handlePresenceLinkClick(item.csid)}
                    />
                  ));
                },
              )
            )}
          </div>
          <Popover.Arrow style={{ fill: 'white' }} width={16} height={8} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
